import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'

import { Button } from '../actions/button'
import { TooltipProvider } from '../feedback/tooltip'
import { cn } from '../lib/cn'
import { IconClose, IconCollapseRail, IconExpandRail, IconMenu } from '../lib/icons'
import { glassSurface, overlayVeil } from '../lib/surfaces'
import { SCREEN_LG, useMediaQuery } from '../lib/use-media-query'
import { SECTION_TITLE_TYPE } from '../typography/typography'
import { CollapsedContext } from './app-shell-nav'
import {
  closeOnFollowedLink,
  ShellHeaderContext,
  ShellHeaderEndContext,
  ShellHeaderStartContext,
  ShellRegionContext,
  type ShellRegion,
} from './shell-region'

// The app frame: one tree per width, not one squeezed (docs/design/frontend-v1.md §4). Labels are
// props, never strings; why the trees are shaped so: packages/ui/docs/barra-in-basso-app-shell.md

export interface AppShellCollapse {
  /** The column shows icons only. Owned by the app, because remembering it is:
   * packages/ui/docs/nessuna-stringa-qui-dentro.md. */
  readonly collapsed: boolean
  readonly onCollapsedChange: (collapsed: boolean) => void
  /** aria-label of the button that narrows the column to its icons. */
  readonly collapseLabel: string
  /** aria-label of the button that gives the column its labels back. */
  readonly expandLabel: string
}

export interface AppShellProps {
  /** The product's mark, linked home: the column's head from `lg`, top-left below if no bar.
   *  Absent, its place is not drawn at all: the column starts at its collapse button and the
   *  header at the drawer button. */
  brand?: React.ReactNode
  /** Who is signed in, or the way in: the foot of the column from `lg`, top-right below. */
  account?: React.ReactNode
  /** Navigation rows. `null` is a state and not an empty value — no column, no drawer, no
   *  bar — for the reason packages/ui/docs/nessuna-stringa-qui-dentro.md gives. */
  sidebar: React.ReactNode
  /** Below `lg`, takes the drawer's place: an `AppShellBottomBar`. With it the header carries
   *  no brand: the bar's home is the way home. */
  bottomBar?: React.ReactNode
  /** Below `lg`, the top-left: an `AppShellBack`, where the brand would have stood. Not
   *  drawn from `lg`, where the column holds the brand and the page its own way back. */
  back?: React.ReactNode
  /** The page opens on a hero: nothing is reserved above it, and the controls float over it.
   *  The hero keeps its contents clear of them (`useShellHeader`), and anything else the page
   *  draws first stands in `BelowHeader`. packages/ui/docs/barra-in-basso-app-shell.md */
  opensOnHero?: boolean
  /** The page stands in one centred column, header and lists together: on a wide screen the rows
   *  stay compact and the air splits on both sides. */
  centredContent?: boolean
  children: React.ReactNode
  /** Title of the mobile panel, read by screen readers. */
  navigationLabel: string
  /** aria-label of the button that opens the panel: it is an icon-only button. */
  openMenuLabel: string
  /** aria-label of the button that closes the panel. */
  closeMenuLabel: string
  /** One object and not four optionals: a `collapsed` without its handler is a column stuck
   *  narrow, and a missing label is an icon-only button nobody can name. Absent means the
   *  column cannot be collapsed at all, and no button is drawn. */
  collapse?: AppShellCollapse
  className?: string
}

export function AppShell({
  brand,
  account,
  sidebar,
  bottomBar,
  back,
  opensOnHero = false,
  centredContent = false,
  children,
  navigationLabel,
  openMenuLabel,
  closeMenuLabel,
  collapse,
  className,
}: AppShellProps) {
  const [panelOpen, setPanelOpen] = React.useState(false)
  // The start of the header, once drawn: a state and not a ref, so the page that portals into it
  // renders again when it arrives, and again with `null` when the header goes.
  const [headerStart, setHeaderStart] = React.useState<HTMLElement | null>(null)
  const [headerEnd, setHeaderEnd] = React.useState<HTMLElement | null>(null)
  const desktop = useMediaQuery(SCREEN_LG)
  const hasNavigation = sidebar != null
  const wide = desktop && hasNavigation
  const hasBottomBar = hasNavigation && bottomBar != null
  const collapsed = collapse?.collapsed === true
  const content = centredContent ? (
    <div className="mx-auto w-full max-w-5xl">{children}</div>
  ) : (
    children
  )

  // The panel lives in the mobile tree only: widening the window must not leave it armed.
  if (desktop && panelOpen) setPanelOpen(false)

  const closePanelOnFollowedLink = closeOnFollowedLink(() => setPanelOpen(false))

  function mobilePanel() {
    if (!hasNavigation || hasBottomBar) return null
    return (
      <DialogPrimitive.Root open={panelOpen} onOpenChange={setPanelOpen}>
        <DialogPrimitive.Trigger asChild>
          <Button variant="glass" size="icon" aria-label={openMenuLabel}>
            <IconMenu aria-hidden="true" />
          </Button>
        </DialogPrimitive.Trigger>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className={cn(overlayVeil, 'bg-volee-dark/60')} />
          <DialogPrimitive.Content
            data-surface="ink"
            className="fixed inset-y-0 left-0 z-(--z-dialog) flex w-3/4 max-w-xs flex-col gap-4 border-r border-border bg-background p-4 pt-[calc(1rem+var(--spacing-safe-top))] pb-[calc(1rem+var(--spacing-safe-bottom))] pl-[calc(1rem+var(--spacing-safe-left))] shadow-overlay data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left"
          >
            <div className="flex items-center justify-between">
              <DialogPrimitive.Title className="text-sm font-medium">
                {navigationLabel}
              </DialogPrimitive.Title>
              <DialogPrimitive.Close asChild>
                <Button variant="ghost" size="icon" aria-label={closeMenuLabel}>
                  <IconClose aria-hidden="true" />
                </Button>
              </DialogPrimitive.Close>
            </div>
            <nav
              aria-label={navigationLabel}
              className="flex flex-col gap-1 overflow-y-auto"
              onClick={closePanelOnFollowedLink}
            >
              {/* Never collapsed: the panel has the room, and it is the primary surface. */}
              <CollapsedContext.Provider value={false}>{sidebar}</CollapsedContext.Provider>
            </nav>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    )
  }

  // At the head of the column and the same height in both states: collapsing must move
  // nothing vertically.
  function collapseButton() {
    if (collapse === undefined) return null
    return (
      <div className={cn('flex', collapsed ? 'justify-center' : 'justify-end')}>
        <Button
          variant="ghost"
          size="sm"
          className="w-9 px-0"
          aria-label={collapsed ? collapse.expandLabel : collapse.collapseLabel}
          aria-expanded={!collapsed}
          onClick={() => collapse.onCollapsedChange(!collapsed)}
        >
          {collapsed ? (
            <IconExpandRail aria-hidden="true" />
          ) : (
            <IconCollapseRail aria-hidden="true" />
          )}
        </Button>
      </div>
    )
  }

  function brandIn(region: ShellRegion) {
    if (brand == null) return null
    return <ShellRegionContext.Provider value={region}>{brand}</ShellRegionContext.Provider>
  }

  function footer(region: ShellRegion) {
    if (account == null) return null
    return (
      <div className="border-t border-border pt-4">
        <ShellRegionContext.Provider value={region}>{account}</ShellRegionContext.Provider>
      </div>
    )
  }

  // The ground under the device's edges, the contents inside them: hence two boxes. Why
  // `h-screen` and not `max-h-`: packages/ui/docs/nessuna-stringa-qui-dentro.md.
  function column() {
    const region: ShellRegion = collapsed ? 'collapsed-column' : 'column'
    return (
      <aside
        data-surface="ink"
        className="sticky top-0 flex h-screen shrink-0 flex-col bg-background pt-safe-top pb-safe-bottom pl-safe-left text-foreground"
      >
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col gap-4',
            collapsed ? 'w-16 px-2 py-4' : 'w-64 p-4',
          )}
        >
          {collapseButton()}
          {brandIn(region)}
          <nav
            aria-label={navigationLabel}
            className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto"
          >
            <CollapsedContext.Provider value={collapsed}>{sidebar}</CollapsedContext.Provider>
          </nav>
          {footer(region)}
        </div>
      </aside>
    )
  }

  // No ground anywhere, or the page's texture is hidden; and a strip that lets taps through
  // the gap between the controls. With no control to hold, no strip.
  function header() {
    const backControl = desktop ? null : back
    const panel = mobilePanel()
    const mark = hasBottomBar ? null : brand
    const holdsControls = backControl != null || panel != null || mark != null
    if (!holdsControls && account == null) return null
    // A page that opens on a hero may hand the header its own way back (`InHeader`), which stands
    // at this start: so the start is drawn for it, empty, and only for it.
    const drawsStart = holdsControls || opensOnHero
    return (
      <ShellRegionContext.Provider value="header">
        <header className="pointer-events-none fixed top-0 right-safe-right left-safe-left z-(--z-header) box-content flex h-(--size-header) items-center justify-between gap-2 px-content-gutter pt-safe-top lg:px-content-gutter-lg">
          {drawsStart ? (
            <div
              ref={setHeaderStart}
              className="pointer-events-auto flex min-w-0 items-center gap-2"
            >
              {backControl}
              {panel}
              {mark}
            </div>
          ) : null}
          {account == null && !opensOnHero ? null : (
            <div className="pointer-events-auto ml-auto flex items-center gap-2">
              {/* Where the page's actions stand, before the account: `contents`, so empty it
                  leaves neither a box nor a gap. */}
              {opensOnHero ? <div ref={setHeaderEnd} className="contents" /> : null}
              {account}
            </div>
          )}
        </header>
      </ShellRegionContext.Provider>
    )
  }

  // The device's edges are cleared here once, so neither the header nor a page measures them.
  // With a bar, the bottom one is inside `--size-bottom-bar`, which the toaster reads too.
  function frameClassName() {
    if (wide) return cn('flex min-h-screen pr-safe-right text-foreground', className)
    return cn(
      'min-h-screen pt-safe-top pr-safe-right pl-safe-left text-foreground',
      !hasBottomBar && 'pb-safe-bottom',
      className,
    )
  }

  function mainClassName() {
    if (wide) {
      return cn(
        'mt-safe-top mb-safe-bottom min-w-0 flex-1 p-content-gutter-lg',
        opensOnHero && 'pt-0',
      )
    }
    return cn(
      'min-w-0 px-content-gutter lg:px-content-gutter-lg',
      !opensOnHero && 'pt-(--size-header)',
      hasBottomBar
        ? 'pb-[calc(var(--size-bottom-bar)+var(--spacing-content-gutter))]'
        : 'pb-content-gutter',
    )
  }

  // Crossing `lg` swaps the frame, never the page: same place, same providers, or a half-filled
  // form remounts empty. packages/ui/docs/barra-in-basso-app-shell.md § «La pagina non si rimonta»
  const floating = wide ? null : header()
  return (
    // The provider here and not in each app: one delay for every tooltip, and the app that
    // forgot it would ship a collapsed rail with no labels at all.
    <TooltipProvider>
      <div className={frameClassName()}>
        {wide ? column() : floating}
        <main className={mainClassName()}>
          <ShellHeaderContext.Provider value={floating !== null}>
            <ShellHeaderStartContext.Provider value={floating === null ? null : headerStart}>
              <ShellHeaderEndContext.Provider value={floating === null ? null : headerEnd}>
                {content}
              </ShellHeaderEndContext.Provider>
            </ShellHeaderStartContext.Provider>
          </ShellHeaderContext.Provider>
        </main>
        {!wide && hasBottomBar ? bottomBar : null}
      </div>
    </TooltipProvider>
  )
}

export interface AppShellBrandProps extends React.ComponentPropsWithoutRef<'a'> {
  /** A plain wordmark on a bare page; the header’s glass remains the default. */
  variant?: 'glass' | 'plain'
  /** Renders the child: the brand is nearly always the router's `Link` to the home. */
  asChild?: boolean
}

const isWord = (node: React.ReactNode): node is string | number =>
  typeof node === 'string' || typeof node === 'number'

// Words would run out of the 64px column; beside a mark they stay for screen readers only.
function wordsForScreenReaders(node: React.ReactNode): React.ReactNode {
  return React.Children.map(node, (child) =>
    isWord(child) ? <span className="sr-only">{child}</span> : child,
  )
}

// With `asChild` the words are the child's own children, not ours.
function wordsForScreenReadersInside(child: React.ReactNode): React.ReactNode {
  if (!React.isValidElement<{ children?: React.ReactNode }>(child)) return child
  return React.cloneElement(child, undefined, wordsForScreenReaders(child.props.children))
}

/** The product's mark. A glass pill in the header, bare in the column; why it is the
 *  package's decision and not each app's: packages/ui/docs/nessuna-stringa-qui-dentro.md. */
export const AppShellBrand = React.forwardRef<HTMLAnchorElement, AppShellBrandProps>(
  ({ className, asChild = false, variant = 'glass', children, ...props }, ref) => {
    const region = React.useContext(ShellRegionContext)
    const Component = asChild ? Slot : 'a'
    const glass = region === 'header' && variant === 'glass'
    const narrow = region === 'collapsed-column'
    const withHiddenWords = asChild
      ? wordsForScreenReadersInside(children)
      : wordsForScreenReaders(children)
    return (
      <Component
        ref={ref}
        data-surface={glass ? 'ink' : undefined}
        className={cn(
          // `text-section-title` already carries the weight: adding one here would make
          // it a local decision again.
          SECTION_TITLE_TYPE,
          'rounded-sm truncate focus-visible:outline-none focus-visible:focus-volee',
          glass &&
            `${glassSurface} inline-flex h-(--size-control-md) min-w-0 items-center rounded-pill px-3 transition-colors hover:bg-background/70`,
          region !== 'header' && 'text-primary-text',
          variant === 'plain' && 'text-foreground',
          narrow && 'flex justify-center',
          className,
        )}
        {...props}
      >
        {narrow ? withHiddenWords : children}
      </Component>
    )
  },
)
AppShellBrand.displayName = 'AppShellBrand'
